import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Snackbar,
} from '@mui/material';
import {
  UploadFile,
  Download,
  Lock as LockIcon,
  Warning,
  Check,
  SwapHoriz,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useSiteReimportStore } from '../../../stores/siteReimportStore';
import { exportSiteCsv } from '../../../api/sitesApi';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { sitesKeys } from '../../../hooks/useSites';
import { plotsKeys } from '../../../hooks/usePlotsQuery';
import { labourItemsKeys } from '../../../hooks/useLabourItemsQuery';
import ReimportSummaryTiles from '../../reimport/ReimportSummaryTiles';
import ReimportMissingLabourItemsCreator from '../../reimport/ReimportMissingLabourItemsCreator';
import ReimportDiffTable from '../../reimport/table/ReimportDiffTable';
import ConfirmDialog from '../../../../../shared/components/ui/ConfirmDialog';

interface SiteReimportPanelProps {
  siteId: number;
  onClose?: () => void;
}

export function SiteReimportPanel({ siteId, onClose }: SiteReimportPanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { closePanel } = useSitePanelState();
  const handleClose = onClose ?? closePanel;

  const [exporting, setExporting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  );
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    selectedFile,
    setSelectedFile,
    validateCsvFile,
    previewData,
    loading,
    error,
    uploadAndPreview,
    clearError,
    executing,
    executeReimport,
    resetAll,
    createdItems,
    clearLabourItemCreationState,
  } = useSiteReimportStore();

  // Reset store state when panel unmounts (X button, ESC, switching panels)
  useEffect(() => {
    return () => {
      useSiteReimportStore.getState().resetAll();
      useSiteReimportStore.getState().clearError();
      useSiteReimportStore.getState().clearLabourItemCreationState();
    };
  }, []);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateCsvFile(file);
    if (validationError) {
      setSnackbarMessage(validationError);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedFile(file);
    clearError();

    try {
      await uploadAndPreview(siteId, file);
      setSnackbarMessage(`File "${file.name}" uploaded successfully!`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch {
      // Error handled by store
    }
  };

  const handleReplaceFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      await exportSiteCsv(siteId);
      setSnackbarMessage('CSV exported successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage('Failed to export CSV');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setExporting(false);
    }
  };

  const handleApplyChanges = async () => {
    if (!previewData || !selectedFile || executing) return;

    try {
      const result = await executeReimport(siteId, selectedFile);
      if (result) {
        // Build success message
        const parts: string[] = ['Re-import complete.'];
        const changes: string[] = [];
        if (result.plotsAdded > 0)
          changes.push(`${result.plotsAdded} plots added`);
        if (result.plotsRemoved > 0)
          changes.push(`${result.plotsRemoved} plots removed`);
        if (result.labourItemsAdded > 0)
          changes.push(`${result.labourItemsAdded} items added`);
        if (result.labourItemsRemoved > 0)
          changes.push(`${result.labourItemsRemoved} items removed`);
        if (result.labourItemsPriceChanged > 0)
          changes.push(`${result.labourItemsPriceChanged} prices updated`);
        if (changes.length > 0) parts.push(changes.join(', ') + '.');
        if (result.protectedItemsSkipped > 0) {
          parts.push(
            `${result.protectedItemsSkipped} protected item${result.protectedItemsSkipped !== 1 ? 's' : ''} skipped.`
          );
        }

        // Set success message in store (persists after panel close)
        useSiteReimportStore.setState({
          successMessage: parts.join(' '),
        });
        resetAll();

        // Invalidate caches and close panel
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: sitesKeys.all }),
          queryClient.invalidateQueries({ queryKey: plotsKeys.all }),
          queryClient.invalidateQueries({
            queryKey: labourItemsKeys.all,
          }),
        ]);

        handleClose();
      }
    } catch {
      // Error handled by store
    }
  };

  const handleCancel = () => {
    if (previewData || selectedFile) {
      setShowConfirm(true);
    } else {
      resetAll();
      clearLabourItemCreationState();
      handleClose();
    }
  };

  const handleConfirmCancel = () => {
    resetAll();
    clearError();
    clearLabourItemCreationState();
    setShowConfirm(false);
    handleClose();
  };

  const hasUnresolvedMissingItems =
    previewData?.missingLabourDefinitions?.some(
      item => !createdItems.has(item)
    ) || false;

  const canApply = Boolean(
    previewData &&
      selectedFile &&
      !executing &&
      !loading &&
      !hasUnresolvedMissingItems
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      data-testid="site-reimport-panel"
    >
      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Stack spacing={3}>
          {/* Upload Zone */}
          <Box data-testid="reimport-panel-upload">
            {!selectedFile || loading ? (
              // No file uploaded — show upload prompt
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                }}
              >
                <UploadFile
                  sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }}
                />
                <Typography variant="body1" gutterBottom>
                  Upload a CSV file to preview changes
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Use the exported CSV format with PLOT NO., NAME, and labour
                  item definitions
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="center"
                  flexWrap="wrap"
                >
                  <Button
                    variant="contained"
                    startIcon={
                      loading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <UploadFile />
                      )
                    }
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                    data-testid="reimport-file-upload-button"
                  >
                    {loading ? 'Processing...' : 'Select CSV File'}
                  </Button>
                  <Button
                    variant="text"
                    startIcon={
                      exporting ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Download />
                      )
                    }
                    onClick={handleExportCsv}
                    disabled={exporting}
                    data-testid="reimport-download-csv-button"
                  >
                    {exporting ? 'Exporting...' : 'Download Current CSV'}
                  </Button>
                </Stack>
              </Box>
            ) : (
              // File uploaded — show filename with replace option
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <UploadFile color="primary" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SwapHoriz />}
                  onClick={handleReplaceFile}
                  disabled={loading}
                >
                  Replace
                </Button>
              </Box>
            )}

            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{ display: 'none' }}
              data-testid="reimport-file-input"
            />
          </Box>

          {/* Error */}
          {error && (
            <Alert severity="error" data-testid="reimport-panel-error">
              {error}
            </Alert>
          )}

          {/* Change Summary — appears after upload */}
          {previewData && (
            <>
              <Box data-testid="reimport-panel-summary">
                <Typography
                  variant="subtitle1"
                  fontWeight="medium"
                  gutterBottom
                >
                  Change Summary
                </Typography>
                <ReimportSummaryTiles summary={previewData.summary} />
              </Box>

              {/* Missing Labour Items — appears if needed */}
              {previewData.missingLabourDefinitions &&
                previewData.missingLabourDefinitions.length > 0 && (
                  <ReimportMissingLabourItemsCreator
                    siteId={siteId}
                    missingItems={previewData.missingLabourDefinitions}
                  />
                )}

              {/* Change Details */}
              <ReimportDiffTable plots={previewData.plots} />

              {/* Protected Items Warning */}
              {previewData.protectionViolations.length > 0 && (
                <Alert
                  severity="warning"
                  icon={<LockIcon />}
                  data-testid="reimport-protection-warning"
                >
                  <Typography variant="subtitle2" gutterBottom>
                    {previewData.protectionViolations.length} item
                    {previewData.protectionViolations.length !== 1
                      ? 's have'
                      : ' has'}{' '}
                    active allocations and will be SKIPPED.
                  </Typography>
                  <Typography variant="body2">
                    Allocated items cannot be modified or removed to protect
                    financial records.
                  </Typography>
                </Alert>
              )}

              {previewData.summary.protectedItemsSkipped > 0 && (
                <Chip
                  icon={<LockIcon />}
                  label={`${previewData.summary.protectedItemsSkipped} protected items will be skipped`}
                  variant="outlined"
                  color="warning"
                  size="small"
                />
              )}

              {/* Irreversibility warning */}
              <Alert
                severity="warning"
                icon={<Warning />}
                data-testid="reimport-irreversibility-warning"
              >
                <Typography variant="subtitle2" gutterBottom>
                  This action cannot be undone
                </Typography>
                <Typography variant="body2">
                  Confirming will apply all changes shown above. Protected items
                  with active allocations will be skipped automatically.
                </Typography>
              </Alert>
            </>
          )}
        </Stack>
      </Box>

      {/* Sticky Confirm Bar */}
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          p: 2,
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          bgcolor: 'background.paper',
        }}
        data-testid="reimport-panel-actions"
      >
        <Button
          variant="text"
          onClick={handleCancel}
          disabled={executing}
          data-testid="reimport-cancel-button"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={
            executing ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Check />
            )
          }
          onClick={handleApplyChanges}
          disabled={!canApply}
          data-testid="reimport-confirm-button"
        >
          {executing ? 'Applying...' : 'Apply Changes'}
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Cancel Confirmation */}
      <ConfirmDialog
        open={showConfirm}
        title="Cancel Re-Import"
        message="Are you sure you want to cancel? All progress including the uploaded file and preview data will be lost."
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowConfirm(false)}
        confirmText="Yes, Cancel"
        cancelText="Continue"
      />
    </Box>
  );
}
